import React, { useState, useEffect, useMemo } from 'react';
import { injectLibraryApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Search, Database, Plus, FileText } from 'lucide-react';

export default function InjectDatabasePicker({ exerciseId, open, onOpenChange, onAdded }) {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [company, setCompany] = useState('all');
  const [selected, setSelected] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Load companies and categories on open
    injectLibraryApi.listCompanies().then(r => setCompanies(r.companies || [])).catch(() => {});
    injectLibraryApi.listCategories().then(r => setCategories(r.categories || [])).catch(() => {});
    // Auto-load all items on open
    setLoading(true);
    setHasSearched(true);
    injectLibraryApi.list({ category: 'all', company: 'all', search: '', limit: 500 })
      .then(res => setItems(res.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const doSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await injectLibraryApi.list({ category, company, search, limit: 200 });
      setItems(res.items || []);
    } catch {
      toast.error('Failed to search inject database');
    } finally {
      setLoading(false);
    }
  };

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected]
  );

  const handleAdd = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one inject');
      return;
    }
    setAdding(true);
    try {
      let count = 0;
      for (const tid of selectedIds) {
        await injectLibraryApi.addToExercise(exerciseId, tid);
        count++;
      }
      toast.success(`Added ${count} inject${count > 1 ? 's' : ''} to exercise`);
      setSelected({});
      onOpenChange(false);
      onAdded?.();
    } catch (err) {
      toast.error(err.message || 'Failed to add injects');
    } finally {
      setAdding(false);
    }
  };

  const toggleItem = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="inject-db-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Select from Inject Database
          </DialogTitle>
          <DialogDescription>
            Search and add pre-existing injects to this exercise
          </DialogDescription>
        </DialogHeader>

        {/* Search controls */}
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search injects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              data-testid="inject-db-search"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[150px]" data-testid="inject-db-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {companies.length > 0 && (
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger className="w-[150px]" data-testid="inject-db-company">
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto">
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={doSearch} disabled={loading} data-testid="inject-db-search-btn">
            <Search className="w-4 h-4 mr-1" />
            Search
          </Button>
        </div>

        {/* Results */}
        <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto" style={{ maxHeight: '45vh' }}>
          <div className="p-2 space-y-1">
            {!hasSearched ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Search the inject database to find templates</p>
                <p className="text-xs mt-1">Use the search bar above or select a category</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">No injects found matching your search</p>
              </div>
            ) : (
              items.map(item => (
                <label
                  key={item.template_id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected[item.template_id] ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50'
                  }`}
                  data-testid={`inject-db-item-${item.template_id}`}
                >
                  <Checkbox
                    checked={!!selected[item.template_id]}
                    onCheckedChange={() => toggleItem(item.template_id)}
                    className="mt-0.5"
                    data-testid={`inject-db-checkbox-${item.template_id}`}
                  />
                  <div className="flex-1 min-w-0" onClick={() => toggleItem(item.template_id)}>
                    <div className="flex items-center gap-2 mb-1">
                      {item.inject_id && (
                        <Badge variant="outline" className="text-xs">{item.inject_id}</Badge>
                      )}
                      {item.category && (
                        <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium line-clamp-1">
                      {item.objective || item.inject_information || 'Untitled inject'}
                    </p>
                    {item.inject_information && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {item.inject_information}
                      </p>
                    )}
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select injects to add'}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleAdd}
              disabled={selectedIds.length === 0 || adding}
              data-testid="inject-db-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {adding ? 'Adding...' : `Add ${selectedIds.length || ''} to Exercise`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
